import { BatteryTypeVariant } from '@pelicargo/types';
import {
  AircraftRestriction,
  AircraftType,
  BatteryType,
  DimensionUnit,
  FreightService,
  HandlingOptions,
  PackingGroup,
  PackingInstruction,
  Section,
  ServiceType,
  WeightUnit,
} from '@prisma/client';

export const OPENAI_REQUEST_INGESTION_PROMPT = `Extract all shipment request information. Respond with a JSON object with snake case keys! Do not rush, take your time and read the instructions carefully.
  Find the following information:
	•	origin_airports (must be array of US airport's three-letter code)
	•	destination_airports (must be array of non-US airport's three-letter code)
	•	cargo_origin (JSON Object)
  •	cargo_origin.line_one (string)
  •	cargo_origin.line_two (string)
  •	cargo_origin.city (string)
  •	cargo_origin.state (string)
  •	cargo_origin.zip_code (string)
  •	cargo_origin.country (string)
  •	line one, line two, city, state, zip code, country
	•	dropoff_by (output this in ISO-Date. default null)
	•	freight_services (options are ${Object.values(FreightService).join(', ')}. default [${FreightService.AFR}])
	•	cargo_type (options are GeneralCargo, Perishable, DangerousGoods, or Batteries. default GeneralCargo)
	•	is_known_shipper (boolean. default true)
	•	commodity (options are:
Machinery, Spare Parts, Electrical Equipment, Nutritional Supplements, Aircraft Parts, Textiles & Fashion, Consumer Commodities, General Cargo, Consolidated Cargo, Foliage, Fresh Flowers, Plants, Seeds, Foodstuff (processed), Milk Powder, Other Foodstuffs, Other Beverages (non-alcohol), Other Drinks, Alcoholic Beverage, Other Perishables, Meat, Seafood, Fish, Crabs (Live), Lobsters (Live), Fruits, Vitamins, Other Fruits, Vegetables, Aircraft Engine, Ship Spares, Car Parts, Equipment, Vehicle / Car, Vaccines, Medical Supplies, Pharmaceuticals, Other, Chemicals, Household Goods & Personal Effects, Other Papers, Documents, Precious Gems, Precious Metals, Other Valuables, Arts, Bank Notes, Machine Parts, Metal (Non-Scrap), Military Equipment (non-munitions), Office Supplies, Cosmetics, Oilfield Equipment, IT Equipment, Cherries, Grapes, Lithium Metal Batteries, Lithium Ion Batteries, Equipment with Lithium Metal Batteries, Equipment with Lithium Ion Batteries, Vehicle / Motorcycle. default Consolidated Cargo)
	•	cargo_details (options are “Weight Per Piece” or “Gross Weight”. Default “Weight Per Piece”)
	•	gross_weight (if cargo details is “Gross Weight” default to gross weight, else default null)
	•	original_dimension_unit (${Object.values(DimensionUnit).join(', ')}. default ${DimensionUnit.in}. This is the unit the dimensions you output are already written in)
	•	original_weight_unit (${Object.values(WeightUnit).join(', ')}. default ${WeightUnit.kg}. This is the unit the weights you output are already written in)
NEVER convert units. Copy every dimension and weight exactly as the message states it and report the unit it was stated in. Do not convert in to cm or lb to kg, and do not normalise to metric: the system converts to metric afterwards, so a number you convert is converted twice and the shipment is stored 2.54x too large. "53x27x31 inches" stays 53, 27, 31 with original_dimension_unit in — never 135x69x79.
	•	aircraft_types (${Object.values(AircraftType).join(', ')}. default to [${Object.values(AircraftType).join(', ')}])
	•	cargo (array of objects)
	•	cargo.quantity (default 1)
	•	cargo.length (number. If decimal, round up)
	•	cargo.width (number. If decimal, round up)
	•	cargo.height (number. If decimal, round up)
	•	cargo.weight (number if cargo details is “Weight Per Piece”)
	•	cargo.handling (array of enums. options are ${Object.values(HandlingOptions).join(', ')}. default [${HandlingOptions.Stackable}]))
	•	service_type (${Object.values(ServiceType).join(', ')}. default [${ServiceType.General}])
	•	un_number (number. default null. Example: “UN3481” is 3481)
	•	class (string. default null)
	•	packing_group (${Object.values(PackingGroup).join(', ')}. default null)
	•	aircraft_restriction (${Object.values(AircraftRestriction).join(', ')}. default null)
	•	packing_instruction (${Object.values(PackingInstruction).join(', ')}. default null)
	•	section (${Object.values(Section).join(', ')}. default null)
	•	battery_type (${Object.values(BatteryType).join(', ')}. default null)
	•	battery_type_variant (${Object.values(BatteryTypeVariant).join(', ')}. default null)
	•	dgdTab (“DGD” or “MSDS”. default null)
Examples for cargo details:
	1.	“2 Skids @ 1359kgs 1 @ 53x27x31 1 @ 49x25x29” Is “Gross Weight”
	2.	“2 Skids 1 @ 53x27x31 700kgs 1 @ 49x25x29 700kgs” Is “Weight Per Piece”
	3.	“Pieces nr , 10 pl Total gross weight,3.215  kg Dimensions,102122152 cms” Is “Gross Weight”
	4.	“Total Cargo: 8 Pallets ( 269ctn ) / G.W 3824 kgs / 17.15 cbm PALLETS : 100×120×186cm - 7 Pallets 100×120×127cm - 1 Pallet” Is “Gross Weight”
Only return the data, do not repeat the question, do not comment, and take your time! Ensure you respect the default values provided if the message doesn't contain the information; this is critical to ensuring this is successful.`;
